import { useEffect, useRef, useState, type MouseEvent, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { FaCheck, FaRegCopy } from "react-icons/fa";

type CopyButtonProps = {
  /** Value that is copied; the button is not rendered without one. */
  value?: string | null;
  /** Accessible name and tooltip, e.g. "Copy email address". */
  label: string;
  className?: string;
};

/** Subtle icon button that copies one value, meant to sit next to that value in a table or list. */
export default function CopyButton({ value, label, className = "" }: CopyButtonProps): ReactElement | null {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | null>(null);

  useEffect(() => () => {
    if (timer.current) window.clearTimeout(timer.current);
  }, []);

  if (!value) return null;

  const handleCopy = async (event: MouseEvent<HTMLButtonElement>) => {
    // The value usually sits in a clickable row; copying must not navigate.
    event.preventDefault();
    event.stopPropagation();
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      if (timer.current) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setCopied(false), 1800);
    } catch (error) {
      console.error("Copy failed", error);
    }
  };

  const currentLabel = copied ? t("common.copied", { defaultValue: "Copied" }) : label;

  return (
    <button
      type="button"
      onClick={handleCopy}
      aria-label={currentLabel}
      title={currentLabel}
      className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-300 dark:text-slate-500 dark:hover:bg-white/10 dark:hover:text-indigo-300 ${className}`}
    >
      {copied ? (
        <FaCheck className="text-emerald-600 dark:text-emerald-300" aria-hidden="true" />
      ) : (
        <FaRegCopy aria-hidden="true" />
      )}
      <span className="sr-only" aria-live="polite">
        {copied ? t("common.copied", { defaultValue: "Copied" }) : ""}
      </span>
    </button>
  );
}
